import {
  AlertMessage,
  AnalyticsEvent as LegacyAnalyticsEvent,
} from '@backstage/core-plugin-api';
import {
  AnalyticsEvent,
  AnalyticsImplementation,
} from '@backstage/frontend-plugin-api';

import { Confidence } from '@spotify-confidence/sdk';

import { ConfidenceUserApi } from './UserApi';

// Extend the Window interface to include Hotjar
declare global {
  interface Window {
    hj?: (
      action: string,
      eventName: string,
      data?: Record<string, any>,
    ) => void;
  }
}

const getUserContext = async (
  userApi?: ConfidenceUserApi,
): Promise<{ account: string; user: string }> => {
  try {
    if (!userApi?.getCredentials) {
      return { account: 'unknown', user: 'unknown' };
    }

    const credentials = await userApi.getCredentials();
    const token = await credentials.getTokenSilently();
    const tokenData = JSON.parse(window.atob(token.split('.')[1]));

    return {
      account: tokenData['https://confidence.dev/account_name'] || 'unknown',
      user: tokenData['https://confidence.dev/user_name'] || 'unknown',
    };
  } catch {
    return { account: 'unknown', user: 'unknown' };
  }
};

export const trackErrorEvent = async (
  message: AlertMessage,
  userApi?: ConfidenceUserApi,
): Promise<void> => {
  if (typeof window === 'undefined' || !window.hj) {
    return;
  }

  try {
    const userContext = await getUserContext(userApi);

    const errorData = {
      severity: message.severity,
      message:
        typeof message.message === 'string'
          ? message.message
          : JSON.stringify(message.message),
      ...userContext,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      windowWidth: window.innerWidth || 0,
      windowHeight: window.innerHeight || 0,
    };

    window.hj('event', 'alert_post', errorData);
  } catch {
    // Silent fallback
  }
};

export class ConfidenceAnalytics implements AnalyticsImplementation {
  confidenceApi: Confidence;
  confidenceUserApi: ConfidenceUserApi;
  accessTokenData: Record<string, string | string[]> | undefined;

  constructor({
    confidenceApi,
    confidenceUserApi,
  }: {
    confidenceApi: Confidence;
    confidenceUserApi: ConfidenceUserApi;
  }) {
    this.confidenceApi = confidenceApi;
    this.confidenceUserApi = confidenceUserApi;
  }

  private async getAccessTokenData() {
    if (this.accessTokenData) {
      return this.accessTokenData;
    }
    const credentials = await this.confidenceUserApi.getCredentials();
    const token = await credentials.getTokenSilently();
    const accessTokenData = JSON.parse(window.atob(token.split('.')[1]));
    this.accessTokenData = accessTokenData;
    return accessTokenData;
  }

  async captureEvent(event: AnalyticsEvent | LegacyAnalyticsEvent) {
    const { action, subject, value, attributes = {} } = event;

    let user;

    try {
      const tokenData = await this.getAccessTokenData();
      // in the next release this user context will be handled by a context provider instead
      user = {
        account: tokenData['https://confidence.dev/account_name'] || '',
        user: tokenData['https://confidence.dev/user_name'] || '',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.debug('Could not get Confidence user credentials');
      user = {};
    }

    try {
      this.confidenceApi
        .withContext({
          user,
        })
        .track(action, {
          subject,
          value: value || 0,
          attributes,
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}
